const DEFAULT_AFTER_LOGIN = '/reportincidentpanel';
const MFA_RETURN_TO_KEY = 'incidentAlertMfaReturnTo';
const PENDING_INCIDENT_KEY = 'incidentAlertPendingIncidentID';

function getOrigin() {
  if (typeof window === 'undefined') return 'http://localhost';
  return window.location.origin;
}

function normalizeBasename(raw) {
  if (!raw) return '';

  let path = String(raw).trim();
  try {
    if (/^https?:\/\//i.test(path)) path = new URL(path).pathname;
  } catch (_) {}

  if (!path || path === '/' || path === '.') return '';
  if (!path.startsWith('/')) path = `/${path}`;
  return path.replace(/\/+$/, '');
}

function stripKnownBasename(pathname = '') {
  let path = String(pathname || '') || '/';
  const configured = normalizeBasename(process.env.PUBLIC_URL);
  const candidates = [configured, '/incidentalert'].filter(Boolean);

  for (const base of candidates) {
    if (path === base) return '/';
    if (path.startsWith(`${base}/`)) return path.slice(base.length) || '/';
  }

  return path;
}

export function safeInternalPath(value) {
  if (!value) return '';

  try {
    const url = new URL(String(value), getOrigin());
    if (url.origin !== getOrigin()) return '';

    const pathname = stripKnownBasename(url.pathname || '/');
    return `${pathname}${url.search || ''}${url.hash || ''}`;
  } catch (_) {
    return '';
  }
}

export function locationToPath(locationLike) {
  if (!locationLike) return '';
  if (typeof locationLike === 'string') return safeInternalPath(locationLike);

  const pathname = locationLike.pathname || '/';
  const search = locationLike.search || '';
  const hash = locationLike.hash || '';
  return safeInternalPath(`${pathname}${search}${hash}`);
}

export function extractIncidentIDFromPath(value) {
  const path = String(value || '');
  const match = path.match(/\/activeincidentpage\/([^/?#]+)/i);
  return match ? decodeURIComponent(match[1]) : '';
}

export function buildActiveIncidentPath(incidentID) {
  const id = String(incidentID || '').trim();
  return id ? `/activeincidentpage/${encodeURIComponent(id)}` : '';
}

export function getIncidentLoginTarget(location) {
  const search = location?.search || '';
  const params = new URLSearchParams(search);
  const statePath = locationToPath(location?.state?.from);
  const requestedReturnTo = safeInternalPath(params.get('returnTo'));
  const currentPath = locationToPath(location);

  const incidentID = String(
    params.get('incidentID') ||
    extractIncidentIDFromPath(requestedReturnTo) ||
    extractIncidentIDFromPath(statePath) ||
    extractIncidentIDFromPath(currentPath) ||
    ''
  ).trim();

  const activePath = buildActiveIncidentPath(incidentID);
  const returnTo = activePath || requestedReturnTo || statePath || DEFAULT_AFTER_LOGIN;

  return {
    incidentID,
    iAccountID: String(params.get('iAccountID') || '').trim(),
    iCampusID: String(params.get('iCampusID') || '').trim(),
    iBuildingID: String(params.get('iBuildingID') || '').trim(),
    activePath,
    returnTo,
  };
}

export function rememberMfaReturnTo(path, incidentID = '') {
  try {
    const safePath = safeInternalPath(path) || DEFAULT_AFTER_LOGIN;
    sessionStorage.setItem(MFA_RETURN_TO_KEY, safePath);

    const id = String(incidentID || extractIncidentIDFromPath(safePath) || '').trim();
    if (id) sessionStorage.setItem(PENDING_INCIDENT_KEY, id);
    else sessionStorage.removeItem(PENDING_INCIDENT_KEY);
  } catch (_) {}
}

export function clearMfaReturnTo() {
  try {
    sessionStorage.removeItem(MFA_RETURN_TO_KEY);
    sessionStorage.removeItem(PENDING_INCIDENT_KEY);
  } catch (_) {}
}

export function getMfaReturnTo(location, fallback = DEFAULT_AFTER_LOGIN) {
  const fromState = locationToPath(location?.state?.from);
  let remembered = '';

  try {
    remembered = safeInternalPath(sessionStorage.getItem(MFA_RETURN_TO_KEY));
  } catch (_) {}

  return fromState || remembered || fallback;
}

export function getCurrentIncidentID() {
  if (typeof window === 'undefined') return '';

  const params = new URLSearchParams(window.location.search || '');
  const fromQuery = String(params.get('incidentID') || '').trim();
  if (fromQuery) return fromQuery;

  const fromPath = extractIncidentIDFromPath(
    `${window.location.pathname || ''}${window.location.search || ''}`
  );
  if (fromPath) return fromPath;

  try {
    return String(sessionStorage.getItem(PENDING_INCIDENT_KEY) || '').trim();
  } catch (_) {
    return '';
  }
}

export function emergencyBypassMatchesLocation(user, location) {
  if (user?.mfaAuthenticationMode !== 'emergency-incident') return true;

  const bypassIncidentID = String(
    user?.mfaEmergencyIncidentID || user?.emergencyIncidentID || ''
  ).trim();

  if (!bypassIncidentID) return false;

  const currentPath = locationToPath(location);
  const currentIncidentID = extractIncidentIDFromPath(currentPath);
  if (currentIncidentID === bypassIncidentID) return true;

  const pathname = currentPath.split(/[?#]/, 1)[0];
  const isConsentRoute =
    pathname === '/incident-alert-consent' ||
    pathname === '/incident-alert-preferences';

  if (!isConsentRoute) return false;

  const fromIncidentID = extractIncidentIDFromPath(
    locationToPath(location?.state?.from)
  );

  return fromIncidentID === bypassIncidentID;
}
